import { useCallback, useEffect, useState } from 'react';
import { LoginScreen } from './screens/LoginScreen';
import { ScanScreen } from './screens/ScanScreen';
import { PackageScreen } from './screens/PackageScreen';
import { RoutesScreen } from './screens/RoutesScreen';
import { RouteDetailScreen } from './screens/RouteDetailScreen';
import { PartialScreen } from './screens/PartialScreen';
import { findByOrder, getSessionContext, scanPackage } from './api/endpoints';
import { setUnauthorizedHandler, tokenStore } from './api/client';
import { ApiError } from './api/types';
import type { SessionDto } from './api/types';
import { queue, startQueue, type ScanLogPayload } from './api/queueRuntime';
import { FEATURES } from './api/features';
import type { PackageInfo } from './domain/types';
import { useT } from './i18n/I18nProvider';
import type { Lang } from './i18n/dictionary';

type View =
  | { name: 'login' }
  | { name: 'scan' }
  | { name: 'package'; code: string; pkg: PackageInfo | null }
  | { name: 'routes' }
  | { name: 'route'; routeNo: string; date: string }
  | { name: 'partial' };

export function App() {
  const { t, applyServerLang } = useT();
  const [view, setView] = useState<View>(() =>
    tokenStore.get() ? { name: 'scan' } : { name: 'login' },
  );
  const [session, setSession] = useState<SessionDto | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const adoptSession = useCallback(
    (next: SessionDto) => {
      setSession(next);
      applyServerLang(next.language as Lang);
    },
    [applyServerLang],
  );

  useEffect(() => {
    setUnauthorizedHandler(() => setView({ name: 'login' }));
    startQueue();
  }, []);

  // Aplikace se spustila s uloženým tokenem – přihlášení neproběhlo,
  // takže kontext směny (a s ním jazyk) je potřeba dotáhnout zvlášť.
  useEffect(() => {
    if (view.name === 'login' || session) return;
    getSessionContext()
      .then(adoptSession)
      .catch((err) => console.warn('[app] kontext směny se nepodařilo načíst:', err));
  }, [view.name, session, adoptSession]);

  const handleResult = useCallback(
    (code: string, pkg: PackageInfo, scanLogged: boolean) => {
      setView({ name: 'package', code, pkg });

      // Data zásilky dorazila, ale zápis „Načteno skladem" na serveru selhal.
      // Zásilku ukážeme tak jako tak – dořeší to fronta na pozadí.
      if (!scanLogged && FEATURES.scanLog) {
        const payload: ScanLogPayload = {
          code,
          statusAtScan: pkg.statusCode,
          routeNumber: pkg.pick.routeNumber,
          vehicle: pkg.pick.vehicleName,
          targetWarehouse: pkg.transfer?.direction ?? null,
          scannedAt: new Date().toISOString(),
        };
        void queue.enqueue('scanLog', payload);
      }
    },
    [],
  );

  const runLookup = useCallback(
    async (code: string, lookup: () => Promise<{ pkg: PackageInfo; scanLogged: boolean }>) => {
      setBusy(true);
      setError(null);
      try {
        const { pkg, scanLogged } = await lookup();
        handleResult(code, pkg, scanLogged);
      } catch (err) {
        if (err instanceof ApiError && err.kind === 'NOT_FOUND') {
          // Nenalezená zásilka je plnohodnotná obrazovka, ne chybová hláška.
          setView({ name: 'package', code, pkg: null });
        } else if (err instanceof ApiError && err.kind === 'UNAUTHORIZED') {
          setView({ name: 'login' });
        } else {
          console.error('[app] načtení zásilky selhalo:', err);
          setError(t('errLoad'));
        }
      } finally {
        setBusy(false);
      }
    },
    [handleResult, t],
  );

  const handleCode = useCallback(
    (code: string) => runLookup(code, () => scanPackage(code)),
    [runLookup],
  );

  // Dohledání podle čísla objednávky ze sheetu „Najít manuálně“.
  // Sken se nezapisuje – skladník zásilku hledá, nedrží ji nutně v ruce.
  const handleOrderNumber = useCallback(
    (orderNumber: string) => runLookup(orderNumber, () => findByOrder(orderNumber)),
    [runLookup],
  );

  // Přehled tras vidí jen mistr. Role dorazí až s kontextem směny, takže
  // do té doby se ikona v liště nezobrazí — raději o vteřinu později než
  // nabídnout skladníkovi obrazovku, na kterou dostane 403.
  const canSeeRoutes = FEATURES.routesOverview && session?.role === 'MASTER';

  const logout = useCallback(() => {
    tokenStore.clear();
    setSession(null);
    setView({ name: 'login' });
  }, []);

  if (view.name === 'login') {
    return (
      <LoginScreen
        onDone={(next) => {
          adoptSession(next);
          setView({ name: 'scan' });
        }}
      />
    );
  }

  if (view.name === 'routes') {
    return (
      <RoutesScreen
        onOpenRoute={(routeNo, date) => setView({ name: 'route', routeNo, date })}
        onBack={() => setView({ name: 'scan' })}
        onLogout={logout}
      />
    );
  }

  if (view.name === 'route') {
    return (
      <RouteDetailScreen
        routeNo={view.routeNo}
        date={view.date}
        onBack={() => setView({ name: 'routes' })}
        onLogout={logout}
      />
    );
  }

  if (view.name === 'partial') {
    return <PartialScreen onBack={() => setView({ name: 'scan' })} onLogout={logout} />;
  }

  if (view.name === 'scan') {
    return (
      <ScanScreen
        onCode={handleCode}
        onLogout={logout}
        onPartial={() => setView({ name: 'partial' })}
        // Dokud kontext směny nedorazí, role není známá a vstup se nenabízí.
        // Neznámá role = běžný skladník; nová hodnota v enumu nesmí nikomu
        // otevřít přehled jen proto, že jí aplikace nerozumí.
        onRoutes={canSeeRoutes ? () => setView({ name: 'routes' }) : undefined}
        busy={busy}
        error={error}
      />
    );
  }

  return (
    <PackageScreen
      pkg={view.pkg}
      scannedCode={view.code}
      onScanNext={() => setView({ name: 'scan' })}
      onFindByOrder={handleOrderNumber}
      onLogout={logout}
    />
  );
}
